/** SCR-ADM-010–013 — consent, timer, prohibitions, mirror vs impersonation (07-08). */

export const IMPERSONATION_DEFAULT_MINUTES = 15;
export const IMPERSONATION_MAX_MINUTES = 60;
export const IMPERSONATION_MIN_MINUTES = 5;
export const IMPERSONATION_TWO_MINUTE_SECONDS = 120;
export const IMPERSONATION_MFA_FRESHNESS_MINUTES = 5;
export const CONSENT_WORDS_MIN = 20;
export const JUSTIFICATION_MIN = 20;
export const CUSTOMER_360_ACCESS_REASON = 'Customer 360';
export const CUSTOMER_360_REVEAL_ACCESS_REASON = 'Customer 360 contact reveal';
export const CUSTOMER_360_ADDRESS_REVEAL_ACCESS_REASON = 'Customer 360 address reveal';
export const FRAUD_INVESTIGATION_EMERGENCY_BASIS = 'FRAUD_INVESTIGATION';

/** 07-08: the reveal control says what it reveals — never a generic "show more". */
export const REVEAL_CONTROL_LABELS = {
  email: 'Show email',
  mobile: 'Show telephone number',
  address: 'Show delivery address',
} as const;

export type RevealField = keyof typeof REVEAL_CONTROL_LABELS;

/** Live MOD-002 in-app titles/bodies (B-A04-05). Operator chrome must match these, not invent a notify API. */
export const IMPERSONATION_SUBJECT_NOTIFY = {
  started: {
    title: 'Support accessed your account',
    body: (justification: string) =>
      `A support session accessed your account. Reason: ${justification}`,
    operator: 'The subject is notified: “Support accessed your account.”',
  },
  ended: {
    title: 'Support session on your account ended',
    body: (justification: string) =>
      `The support session on your account has ended. Reason: ${justification}`,
    operator: 'The subject is notified: “Support session on your account ended.”',
  },
  suppressed:
    'Subject notification is suppressed because this session is a documented fraud investigation.',
} as const;

export const CONSENT_CHANNELS = [
  { id: 'in_channel', label: 'In-channel confirmation' },
  { id: 'recorded_call', label: 'Recorded call' },
  { id: 'written', label: 'Written' },
] as const;

export type ConsentChannelId = (typeof CONSENT_CHANNELS)[number]['id'];

export const IMPERSONATION_PROHIBITED_ACTIONS = [
  { id: 'payment_instruments', label: 'Payment instruments' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'account_closure', label: 'Account closure' },
  { id: 'order_placement', label: 'Order placement' },
  { id: 'corporate_approvals', label: 'Corporate approvals' },
] as const;

export type ImpersonationProhibitedActionId =
  (typeof IMPERSONATION_PROHIBITED_ACTIONS)[number]['id'];

export type ImpersonationScope = 'read' | 'write';

export type MirrorVsImpersonation = {
  defaultTool: 'mirror';
  mirror: string;
  impersonation: string;
};

export function clampImpersonationMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return IMPERSONATION_DEFAULT_MINUTES;
  return Math.min(
    IMPERSONATION_MAX_MINUTES,
    Math.max(IMPERSONATION_MIN_MINUTES, Math.round(minutes)),
  );
}

export function defaultImpersonationExpiryIso(now = Date.now()): string {
  return new Date(now + IMPERSONATION_DEFAULT_MINUTES * 60_000).toISOString();
}

export function remainingSeconds(expiresAt: string | null | undefined, now = Date.now()): number {
  if (!expiresAt) return 0;
  const ends = Date.parse(expiresAt);
  if (!Number.isFinite(ends)) return 0;
  return Math.max(0, Math.floor((ends - now) / 1000));
}

export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function isTwoMinuteWarning(totalSeconds: number): boolean {
  return totalSeconds > 0 && totalSeconds <= IMPERSONATION_TWO_MINUTE_SECONDS;
}

export function isImpersonationProhibited(
  actionId: string,
): actionId is ImpersonationProhibitedActionId {
  return IMPERSONATION_PROHIBITED_ACTIONS.some((row) => row.id === actionId);
}

export function refuseProhibitedAction(actionId: string): { allowed: false; reason: string } {
  const row = IMPERSONATION_PROHIBITED_ACTIONS.find((item) => item.id === actionId);
  const label = row?.label ?? 'This action';
  return {
    allowed: false,
    reason: `${label} are refused during impersonation, regardless of grant.`,
  };
}

export function evaluateImpersonationAction(actionId: string): {
  allowed: boolean;
  reason: string;
} {
  if (isImpersonationProhibited(actionId)) {
    return refuseProhibitedAction(actionId);
  }
  return { allowed: true, reason: '' };
}

export function mirrorVsImpersonationCopy(): MirrorVsImpersonation {
  return {
    defaultTool: 'mirror',
    mirror:
      'Support mirror is the default: a read-only rendering of what the user sees, without assuming their identity.',
    impersonation:
      'Impersonation assumes the user session only when the mirror cannot show the behaviour, or a change the user cannot make themselves.',
  };
}

export type ConsentInput = {
  channel: string;
  words: string;
  recordedAt: string;
};

export type ConsentValidation =
  | { ok: true; evidence: string; recordedAt: string; channel: ConsentChannelId }
  | { ok: false; errors: Partial<Record<'channel' | 'words' | 'recordedAt', string>> };

export function validateConsentStatement(input: ConsentInput): ConsentValidation {
  const errors: Partial<Record<'channel' | 'words' | 'recordedAt', string>> = {};
  const channel = CONSENT_CHANNELS.find((row) => row.id === input.channel);
  if (!channel) {
    errors.channel = 'Record the channel used to obtain consent.';
  }
  const words = input.words.trim();
  if (words.length < CONSENT_WORDS_MIN) {
    errors.words = `Record the words used (at least ${CONSENT_WORDS_MIN} characters). Consent is a statement, not a checkbox.`;
  }
  const recordedAt = input.recordedAt.trim();
  const recordedMs = Date.parse(recordedAt);
  if (!recordedAt || !Number.isFinite(recordedMs)) {
    errors.recordedAt = 'Record the time consent was obtained.';
  }
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  const channelId = channel!.id;
  return {
    ok: true,
    channel: channelId,
    recordedAt,
    evidence: JSON.stringify({
      channel: channelId,
      channelLabel: channel!.label,
      words,
      recordedAt,
    }),
  };
}

export type ImpersonationStartInput = {
  subjectAccountId: string;
  supportCaseId: string;
  justification: string;
  consent: ConsentInput;
  scope: ImpersonationScope;
  writeJustification: string;
  mfaCode?: string;
  /** True after start returns ERR-AUTH-0051 (MFA older than 5 minutes). */
  mfaRequired?: boolean;
  durationMinutes?: number;
  emergencyBasis?: string;
};

export type ImpersonationStartValidation =
  | {
      ok: true;
      body: {
        subjectAccountId: string;
        supportCaseId: string;
        justification: string;
        consentEvidence: string;
        mode: 'READ_ONLY' | 'READ_WRITE';
        durationMinutes: number;
        emergencyBasis?: string;
      };
      durationMinutes: number;
    }
  | {
      ok: false;
      errors: Record<string, string>;
    };

export function validateImpersonationStart(
  input: ImpersonationStartInput,
): ImpersonationStartValidation {
  const errors: Record<string, string> = {};
  if (!input.subjectAccountId.trim()) {
    errors.subjectAccountId = 'A subject account is required.';
  }
  if (!input.supportCaseId.trim()) {
    errors.supportCaseId = 'An open case reference is required.';
  }
  const justification = input.justification.trim();
  if (justification.length < JUSTIFICATION_MIN) {
    errors.justification = `State why impersonation is necessary (at least ${JUSTIFICATION_MIN} characters).`;
  }
  const consent = validateConsentStatement(input.consent);
  if (!consent.ok) {
    Object.assign(errors, consent.errors);
  }
  if (input.mfaRequired && !input.mfaCode?.trim()) {
    errors.mfaCode = `MFA must be fresh within ${IMPERSONATION_MFA_FRESHNESS_MINUTES} minutes. Enter your authentication code.`;
  }
  if (input.scope === 'write') {
    if (input.writeJustification.trim().length < JUSTIFICATION_MIN) {
      errors.writeJustification =
        'Write impersonation needs a separate elevation and a per-action justification.';
    }
  }
  if (input.durationMinutes != null && !Number.isFinite(Number(input.durationMinutes))) {
    errors.durationMinutes = `Duration must be ${IMPERSONATION_MIN_MINUTES}–${IMPERSONATION_MAX_MINUTES} minutes.`;
  }
  if (Object.keys(errors).length > 0 || !consent.ok) {
    return { ok: false, errors };
  }
  const durationMinutes = clampImpersonationMinutes(
    input.durationMinutes ?? IMPERSONATION_DEFAULT_MINUTES,
  );
  const emergencyBasis = input.emergencyBasis?.trim();
  return {
    ok: true,
    durationMinutes,
    body: {
      subjectAccountId: input.subjectAccountId.trim(),
      supportCaseId: input.supportCaseId.trim(),
      justification,
      consentEvidence: consent.evidence,
      mode: input.scope === 'write' ? 'READ_WRITE' : 'READ_ONLY',
      durationMinutes,
      ...(emergencyBasis ? { emergencyBasis } : {}),
    },
  };
}

export type MirrorDeepLinkInput = {
  subjectKind: string | null;
  subjectId: string | null;
};

export type MirrorDeepLinkResult =
  | { ok: true; subjectKind: 'customer' | 'business'; subjectId: string }
  | { ok: false; happened: string; why: string; next: string };

export function revalidateMirrorDeepLink(input: MirrorDeepLinkInput): MirrorDeepLinkResult {
  const kind = input.subjectKind;
  const id = input.subjectId?.trim() ?? '';
  if (!kind || !id) {
    return {
      ok: false,
      happened: 'This support mirror link is missing its subject.',
      why: 'The mirror is deep-link only and must be opened from a case or a 360 view with a customer or merchant id.',
      next: 'Return to the case or 360 view and open the mirror from there.',
    };
  }
  if (kind !== 'customer' && kind !== 'business') {
    return {
      ok: false,
      happened: 'This support mirror link names an unsupported subject type.',
      why: `“${kind}” is not a customer or merchant subject.`,
      next: 'Return to the case or 360 view and open the mirror from a customer or merchant record.',
    };
  }
  return { ok: true, subjectKind: kind, subjectId: id };
}

export function canReenterEndedImpersonation(
  endedSessionId: string | null,
  activeSessionId: string | null,
): boolean {
  if (activeSessionId) return true;
  if (endedSessionId) return false;
  return true;
}

export function isMaskedContact(value: string | null | undefined): boolean {
  if (!value) return true;
  return /\*|masked|•/i.test(value);
}

/** GET /admin/v1/customers/{id} returns 400 ERR-PLAT-1002 without this header. */
export function customer360AccessReason(input: {
  caseId?: string | null;
  storedReason?: string | null;
  revealed?: boolean;
  addressRevealed?: boolean;
}): string {
  const caseId = input.caseId?.trim();
  const stored = input.storedReason?.trim();
  const revealLabel = input.addressRevealed
    ? CUSTOMER_360_ADDRESS_REVEAL_ACCESS_REASON
    : input.revealed
      ? CUSTOMER_360_REVEAL_ACCESS_REASON
      : CUSTOMER_360_ACCESS_REASON;
  if (caseId) {
    return `Case ${caseId}: ${revealLabel}`;
  }
  if (stored) {
    if (input.addressRevealed) return `${stored} (address reveal)`;
    if (input.revealed) return `${stored} (contact reveal)`;
    return stored;
  }
  return revealLabel;
}

export function regionUnavailableCopy(region: string): string {
  return `${region} could not be loaded. The request failed. Try again, or continue with the rest of this record.`;
}

export function walletEmptyKind(
  wallet:
    | {
        state?: string | null;
        balanceMinor?: number | null;
      }
    | undefined,
): 'missing' | 'none' | 'present' {
  if (!wallet) return 'missing';
  if ((wallet.state ?? '').toUpperCase() === 'NONE' && !wallet.balanceMinor) return 'none';
  return 'present';
}

export function screenPermissionDeniedCopy(screen: '010' | '011' | '012' | '013'): string {
  switch (screen) {
    case '010':
      return 'You cannot open Customer 360. Ask a Support Lead to grant customer profile read.';
    case '011':
      return 'You cannot open Merchant 360. Ask a Support Lead to grant business read.';
    case '012':
      return 'You cannot open the support mirror. Ask a Support Lead to grant mirror read.';
    case '013':
      return 'You cannot start impersonation. Ask a Support Lead to grant session impersonation.';
  }
}

export function visibleContactValue(
  value: string | null | undefined,
  operatorRevealed: boolean,
): { display: string; unmasked: boolean } {
  if (!operatorRevealed) {
    return {
      display: value && isMaskedContact(value) ? value : 'Masked',
      unmasked: false,
    };
  }
  if (!value) return { display: 'Not held', unmasked: true };
  return { display: value, unmasked: !isMaskedContact(value) };
}

/** Same rule as MOD-002 `shouldSuppressImpersonationSubjectNotification`. */
export function shouldSuppressImpersonationSubjectNotification(
  emergencyBasis?: string | null,
): boolean {
  if (!emergencyBasis) return false;
  return /fraud[\s_-]*invest/i.test(emergencyBasis);
}

export function impersonationNotifyOperatorCopy(
  phase: 'started' | 'ended',
  emergencyBasis?: string | null,
): string {
  if (shouldSuppressImpersonationSubjectNotification(emergencyBasis)) {
    return IMPERSONATION_SUBJECT_NOTIFY.suppressed;
  }
  return IMPERSONATION_SUBJECT_NOTIFY[phase].operator;
}
